/**
 * Edgeguard particle effects — entry burst, fire impact lines, end flash.
 *
 * Cyan palette (matches edgeguard beam color). Lighter than burst's 6-layer
 * choreography — edgeguard is a follow-up, not a primary event.
 *
 * Sakurai audit fixes:
 * - A. Phase entry had zero particles → 4-layer entry choreography
 * - B. Beam fire had no staggered timing → delayed end flash
 * - C. Beam sparks were all bright ADD → mixed dark/bright (handled in phase-edgeguard.ts)
 * - D. Muzzle had no dark framing → dark framing (handled in phase-edgeguard.ts)
 * - E. No impact lines at fire → radial fire lines
 * - F. Entry flash too subtle → bumped alpha (handled in constants.ts)
 */

import { configParticle, type ParticleCore } from './types';

// Cyan palette constants (matching EDGEGUARD / BEAM_COLOR)
const CYAN = 0x00ffff;
const CYAN_DARK = 0x001122;

/**
 * Edgeguard Entry — burst when edgeguard phase starts.
 * 4-layer Sakurai choreography (lighter than burst's 6 — follow-up, not primary):
 * converging cyan motes → radial speed lines → dark contrast cavity → shockwave ring.
 */
export function spawnEdgeguardEntry(core: ParticleCore, cx: number, cy: number, playerColor: number): void {
    // === Layer 1: Converging cyan motes — energy gathering inward toward attacker ===
    const moteCount = 12;
    for (let i = 0; i < moteCount; i++) {
        const angle = (i / moteCount) * Math.PI * 2;
        const dist = 55 + Math.random() * 20;
        const spawnX = cx + Math.cos(angle) * dist;
        const spawnY = cy + Math.sin(angle) * dist;
        const speed = 3 + Math.random() * 2;
        const moteColor = Math.random() < 0.2 ? 0xffffff : CYAN;
        const p = core.getInactive();
        if (!p) continue;
        configParticle(p, core, {
            x: spawnX,
            y: spawnY,
            vx: -Math.cos(angle) * speed, // inward
            vy: -Math.sin(angle) * speed,
            color: moteColor,
            life: 0.25 + Math.random() * 0.1,
            scale: 0.3 + Math.random() * 0.2,
            alpha: 0.8,
            friction: 0.92,
            freezeImmune: true,
        });
    }

    // === Layer 2: Radial speed lines — Sakurai "straight lines vs curves" ===
    const lineCount = 8;
    for (let i = 0; i < lineCount; i++) {
        const angle = (i / lineCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
        const life = 0.1 + Math.random() * 0.06;
        const lineColor = Math.random() < 0.25 ? 0xffffff : CYAN;
        core.impactLines.push({
            x: cx, y: cy,
            angle,
            innerRadius: 10,
            outerRadius: 35 + Math.random() * 20,
            expandSpeed: 80 + Math.random() * 40,
            color: lineColor,
            lineWidth: 1.5 + Math.random() * 0.8,
            life,
            maxLife: life,
            freezeImmune: true,
        });
    }

    // === Layer 3: Dark contrast cavity — Sakurai "mix in darker elements" ===
    for (let i = 0; i < 4; i++) {
        const angle = Math.random() * Math.PI * 2;
        const p = core.getInactive();
        if (!p) continue;
        configParticle(p, core, {
            x: cx + (Math.random() - 0.5) * 10,
            y: cy + (Math.random() - 0.5) * 10,
            vx: Math.cos(angle) * (0.4 + Math.random() * 0.6),
            vy: Math.sin(angle) * (0.4 + Math.random() * 0.6),
            color: CYAN_DARK,
            life: 0.15 + Math.random() * 0.1,
            scale: 0.8 + Math.random() * 0.5,
            alpha: 0.5,
            friction: 0.9,
            rotationSpeed: 0,
            addBlend: false,
            hard: false,
            freezeImmune: true,
        });
    }

    // === Layer 4: Expanding shockwave ring — cyan burst circle ===
    core.spawnShockwave(cx, cy, CYAN, 50, 90, 2.0, 0.15);
}

/**
 * Edgeguard Fire Lines — radial impact lines at muzzle on beam fire.
 * 8 lines spread +-45deg from fire angle. Sakurai "straight lines vs curves."
 */
export function spawnEdgeguardFireLines(core: ParticleCore, x: number, y: number, angle: number): void {
    const lineCount = 8;
    for (let i = 0; i < lineCount; i++) {
        const spread = (Math.random() - 0.5) * Math.PI * 0.5; // +-45deg
        const lineAngle = angle + spread;
        const life = 0.1 + Math.random() * 0.06;
        const lineColor = Math.random() < 0.3 ? 0xffffff : CYAN;
        core.impactLines.push({
            x, y,
            angle: lineAngle,
            innerRadius: 10,
            outerRadius: 35 + Math.random() * 15,
            expandSpeed: 100 + Math.random() * 40,
            color: lineColor,
            lineWidth: 2.0 + Math.random() * 1.0,
            life,
            maxLife: life,
            freezeImmune: false,
        });
    }
}

/**
 * Edgeguard End Flash — delayed terminus punctuation.
 * Sakurai Phase 4: white soft glow + dark pop backing via pendingBlasts (+150ms).
 */
export function spawnEdgeguardEndFlash(core: ParticleCore, x: number, y: number): void {
    core.pendingBlasts.push({
        timestamp: Date.now() + 150,
        callback: () => {
            // White soft glow
            const flash = core.getInactive();
            if (flash) {
                configParticle(flash, core, {
                    x, y,
                    color: 0xffffff,
                    life: 0.08,
                    scale: 3.0,
                    alpha: 0.6,
                    rotation: 0,
                    rotationSpeed: 0,
                    hard: false,
                });
            }
            // Dark pop backing
            const darkPop = core.getInactive();
            if (darkPop) {
                configParticle(darkPop, core, {
                    x, y,
                    color: CYAN_DARK,
                    life: 0.1,
                    scale: 2.0,
                    alpha: 0.45,
                    rotation: 0,
                    rotationSpeed: 0,
                    addBlend: false,
                    hard: false,
                });
            }
        },
    });
}
